<template>
  <div class="chirashi-iframe" ref="root">
    <error-page-content
      v-if="isError"
      :status-code="statusCode"
      :api-code="apiCode"
      :summary="errorSummary"
      :message="errorMessage"
      :is-iframe="true"
    />
    <component
      :is="tagName"
      v-else-if="!isEmpty"
      :items="chirashiStore.iframFlyers"
      :shop-id="shopId"
      :utm-source="utmSource"
      :location="getLocation()"
      :aspect-fit="aspectFit"
      :display-date="displayDate"
      @height:update="updateHeight"
      @click="onClick($event)"
      @intersect="handleFlyerIndexCardIntersect"
    />
    <component :is="noContentTagName" v-else text="チラシ" />
  </div>
</template>

<script setup>
import DaieiFlyerCards from '@/components/DaieiFlyerCards'
import NoContent from '@/components/NoContent'
import ErrorPageContent from '@/components/ErrorPageContent'
import MaxvaluKantouFlyerCard from '@/components/MaxvaluKantouFlyerCard'
import BaseFlyerCards from '@/components/BaseFlyerCards'
import EntetsuFlyerCards from '@/components/EntetsuFlyerCards'

import { useRoute } from 'vue-router'
import { useChirashiStore } from '@/stores/chirashi'
import { useMyStoreStore } from '@/stores/myStore'
import { useImpressionStore } from '@/stores/impression'

const chirashiStore = useChirashiStore()
const myStoreStore = useMyStoreStore()
const impressionStore = useImpressionStore()
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const root = ref(null)
const shopId = ref(null)
const utmSource = ref(null)
const aspectFit = ref(false)
const displayDate = ref(true)
const isError = ref(false)
const errorOptions = ref({})
const isEmpty = ref(false)
const apiStatusCodes = ref({
  10000: 422,
  10003: 400,
  10004: 400,
  10301: 400,
  10302: 400,
  10311: 400,
  99999: 500,
  10202: 422,
  10209: 422,
  10001: 401,
  10002: 401,
  10005: 400,
  10006: 400,
  10007: 400,
  10051: 400,
  90001: 500,
})

const tagName = computed(() => {
  if (runtimeConfig.public.chainKey === 'daiei') {
    return resolveComponent('DaieiFlyerCards')
  } else if (runtimeConfig.public.chainKey === 'entetsu-store') {
    return resolveComponent('EntetsuFlyerCards')
  } else if (runtimeConfig.public.chainKey === 'maxvalu-kantou') {
    return resolveComponent('MaxvaluKantouFlyerCards')
  } else {
    return resolveComponent('BaseFlyerCards')
  }
})


const noContentTagName = computed(() => {
  return runtimeConfig.public.chainKey === 'daiei'
    ? resolveComponent('NoContent')
    : runtimeConfig.public.chainKey === 'maxvalu-kantou'
    ? resolveComponent('NoContentMaxvaluKantou')
    : resolveComponent('NoContentIframe')
})

const statusCode = computed(() => {
  let statusCode = ''
  if (!apiCode.value) {
    statusCode = errorOptions.value.statusCode
  } else if (apiStatusCodes.value[apiCode.value]) {
    statusCode = apiStatusCodes.value[apiCode.value]
  } else {
    statusCode = 500
  }
  return statusCode
})

const apiCode = computed(() => {
  return errorOptions.value?.name &&
    errorOptions.value?.name.match(/apiError/)
    ? Number(errorOptions.value.name.replace(/apiError /, ''))
    : 0
})

const errorSummary = computed(() => {
  if (statusCode.value === 500) {
    return 'エラーが発生しました'
  }
  return apiCode.value
    ? errorOptions.value.message.split('|')[0]
    : '対象のコンテンツは存在しません'
})

const errorMessage = computed(() => {
  if (statusCode.value === 500) {
    return 'ただいま大変混雑しています。しばらく待ってから再度アクセスしてください。'
  }
  return apiCode.value
    ? errorOptions.value.message.split('|')[1]
    : 'ページが存在しない、またはページが削除された可能性があります。\\nホームにもどり、ご覧になりたい情報をお探しください。'
})

const isFlyerViewer = computed(() => {
  return true
})

const init = async () => {
  shopId.value = route.query['shop-id']
  utmSource.value = route.query.utm_source
  aspectFit.value = route?.query['aspect-fit'] === '1'
  displayDate.value = route?.query['display-date'] !== '0'
  myStoreStore.$patch({ activeMyStoreId: shopId.value })
  if (utmSource.value) {
    myStoreStore.$patch({ utmSource: utmSource.value })
  } else {
    myStoreStore.$patch({ utmSource: null })
  }
  try {
    await chirashiStore.fetchIframeItems({ shopCode: shopId.value })
    if (!chirashiStore.iframFlyers.length) isEmpty.value = true
  } catch (err) {
    errorOptions.value = err
    isError.value = true
  } finally {
    setTimeout(() => {
      updateHeight()
    }, 10)
  }
}

const updateHeight = () => {
  const height = root.value.clientHeight
  window.parent.postMessage(['setHeight', height], '*')
}

const onClick = (obj) => {
  // console.log(obj)
  chirashiStore.$patch({ activeDetailId: obj.detailId })
  chirashiStore.$patch({ isBlank: true })
  window.open(obj.link, '_blank')
}

const handleFlyerIndexCardIntersect = ({ id, detailId, shopId, location }) => {
  const activeShop = GETSHOP(shopId, myStoreStore.stores)
  impressionStore.sendFlyerListImpression({
    id,
    detailId,
    shopId: activeShop?.shopId,
    location,
  })
}
const getLocation = () => {
  const location = {
    display_name: 'chirashi-iframe',
    area_name: 'chirashi-list',
    area_line_number: 1,
    element_name: 'chirashi-list',
    element_line_number: 0,
  }
  return location
}

// CREATED
chirashiStore.initIframe()

onMounted(() => {
  window.addEventListener('resize', updateHeight)
  const isShopId = route.query['shop-id']
  if (!isShopId) {
    errorOptions.value = { statusCode: 404 }
    isError.value = true
    return
  }
  init()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateHeight)
})
</script>

<style lang="scss" scoped>
.chirashi-iframe {
  display: block;
}
</style>
